import { Box, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import styled from 'styled-components'
import { tokenHref } from '../config'
import { fmtUsd } from '../format'
import { Icon } from '../icons'
import type { Suggestion, TokenSearchResult } from '../search'
import { Pill } from '../ui'
import TokenLogo from './TokenLogo'
import { ListCad1Tick, ListTick } from './VerifyTicks'

const List = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 6px;
  display: grid;
  gap: 4px;
  border-radius: 16px;
  background: rgba(10, 14, 34, 0.96);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  max-width: 640px;
`
/* the row is one link stretched over it (the header search's arrow keys walk these links); the tick sits above it with its own card */
const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 8px 10px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.text};
  &:hover, &:focus-within { background: rgba(127, 212, 232, 0.1); }
`
const RowLink = styled.a`
  min-width: 0;
  color: inherit;
  text-decoration: none;
  outline: none;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
  }
`
/* the symbol, then the tick: a long symbol ends in an ellipsis before it */
const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
`
const Ellipsis = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Side = styled.div`
  display: grid;
  justify-items: end;
  gap: 4px;
  min-width: 0;
`

function Mark({ s }: { s: Suggestion }) {
  if (s.lowLiquidity) return <Pill $tone="warning" data-testid="tp-suggest-low">Low liquidity / unverified</Pill>
  // a token the list knows only by its basic page: not vetted, not "listed on CADINUdex"; the honest label is the page's own
  if (s.source === 'list' && !s.verified) return <Pill $tone="muted" data-testid="tp-suggest-basic" title="A basic page: links from the project’s public profiles, not verified">Basic page</Pill>
  return (
    <Pill $tone="success" data-testid="tp-suggest-verified" title={s.source === 'list' ? 'Listed on CADINUdex' : 'Pool verified on chain'}>
      <Icon name="shieldCheck" size={14} /> {s.source === 'list' ? 'Listed' : 'Verified pool'}
    </Pill>
  )
}

/**
 * The suggestion list under the search box: local list matches first, then what GeckoTerminal (or DexScreener) lists on BSC for the query,
 * each row a link to /token/<address> with the token's one tick (gold / blue, from the list) beside its symbol. A pasted address shows the token read from its contract with its verified pools, or says it is not
 * a token. When no source can answer it says so ("search is busy"), never "no match".
 */
export default function SearchSuggest({ rows, result, loading, failed, isAddress }: { rows: Suggestion[]; result?: TokenSearchResult; loading: boolean; failed: boolean; isAddress: boolean }) {
  const busy = failed || Boolean(result?.busy)
  if (isAddress && result?.notToken) {
    return (
      <List data-testid="tp-suggest">
        <li data-testid="tp-suggest-not-token" style={{ padding: '10px 12px' }}>
          <Text bold>This address is not a token</Text>
          <Text fontSize="13px" color="textSubtle">It answers no ERC-20 name, symbol or decimals on BNB Smart Chain. Check that you pasted a token contract, not a wallet or a pool.</Text>
        </li>
      </List>
    )
  }
  if (!rows.length && !loading && !busy) return null
  return (
    <List data-testid="tp-suggest" aria-busy={loading}>
      {rows.map((s) => (
        <li key={s.address}>
          <Row data-testid="tp-suggest-row" data-source={s.source} data-tick={s.tick ?? undefined}>
            <TokenLogo srcs={[s.logoUrl]} address={s.address} symbol={s.symbol} size={36} />
            <Box style={{ minWidth: 0 }}>
              <NameLine>
                <Link href={tokenHref(s.address)} passHref legacyBehavior>
                  <RowLink aria-label={`${s.symbol}, ${s.name}`} data-testid="tp-suggest-link">
                    <Ellipsis bold>{s.symbol}</Ellipsis>
                  </RowLink>
                </Link>
                <ListTick address={s.address} verified={s.tick} testId="tp-suggest-tick" />
                <ListCad1Tick cad1={s.cad1} testId="tp-suggest-cad1" />
              </NameLine>
              <Ellipsis fontSize="12px" color="textSubtle">{s.name}{s.bestDex ? ` · ${s.bestDex}` : ''}</Ellipsis>
              {isAddress && s.pools?.length ? (
                <Ellipsis fontSize="12px" color="textSubtle" data-testid="tp-suggest-pools">{s.pools.map((p) => `${p.dex} ${p.pair}`).join(' · ')}</Ellipsis>
              ) : null}
            </Box>
            <Side>
              <Text fontSize="13px" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.liquidityUsd === null ? '—' : fmtUsd(s.liquidityUsd)}</Text>
              <Mark s={s} />
            </Side>
          </Row>
        </li>
      ))}
      {loading && (
        <li data-testid="tp-suggest-loading" style={{ padding: '8px 12px' }}>
          <Text fontSize="13px" color="textSubtle">{isAddress ? 'Reading this address on chain…' : 'Searching BNB Smart Chain…'}</Text>
        </li>
      )}
      {!loading && busy && (
        <li data-testid="tp-suggest-busy" style={{ padding: '8px 12px' }}>
          <Text fontSize="13px" color="warning">Search is busy, try again in a minute.</Text>
        </li>
      )}
    </List>
  )
}
